import { useToast } from '@/composables/useToast.js';
import apiClient from '@/utils/axios.js';
import { newClientRequestId } from '@/utils/clientRequestId.js';
import { defineStore } from 'pinia';
import { toRaw } from 'vue';

import offlineDB, {
    cacheVariantAvailability,
    captureOfflineCacheWriteEpoch,
    clearStoreState,
    finalizeSaleAllocations,
    loadStoreState,
    resetAllReservations,
    saveStoreState,
    syncBranchStock,
} from '@/indexedDB/offlineDB.js';
import { canMutateOnline } from '@/services/connectivityState.js';
import {
    calculateSaleSummary,
    exclusiveRateFrom,
    lineDiscountCents,
    lineGrossCents,
    normalizeNonNegativeNumber,
    percentOf,
    roundMoney,
    toBasisPoints,
} from '@/services/saleCalculations.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';
import { todayLocal } from '@/utils/businessDate.js';

let persistChain = Promise.resolve();
let persistTimer = null;
let persistGeneration = 0;
let customerRequestSequence = 0;
const latestCustomerRequestByTab = new Map();
let reservationGeneration = 0;
let reservationChain = Promise.resolve();

/**
 * The rate a cart line carries.
 *
 * The catalogue prices goods tax-inclusive — `branch_variants.sales_price` is
 * the shelf price — but the sale chain is a §2(46) one: the rate, both discounts
 * and the value of supply all exclude sales tax, and the tax is added back at
 * the end. Converting here rather than in the calculator means the figure the
 * cashier sees, the base a percentage discount is taken from, the cap on a rupee
 * discount and the `amount` submitted to CreateSaleJob are all the same number.
 *
 * A line whose goods carry no tax converts to itself.
 */
const cartRateFrom = (product, taxPercentage) => exclusiveRateFrom(product?.rate ?? 0, taxPercentage);

/**
 * Bring a restored cart line onto the ex-tax rate convention.
 *
 * `rate` used to be the tax-inclusive shelf price. A draft persisted before that
 * changed would be read as though it already excluded tax, quoting the customer
 * the shelf price plus tax again — an 18% over-charge on a cart nobody retyped.
 * `rate_excludes_tax` marks a line that has already been converted, so this runs
 * once per stale line and never again.
 */
const migrateRestoredItem = (item) => {
    if (!item || item.rate_excludes_tax) return item;

    return {
        ...item,
        rate: exclusiveRateFrom(item.rate ?? 0, Number(item.tax_percentage ?? 0) || 0),
        rate_excludes_tax: true,
    };
};

// Quantities are decimal(12,2) server-side, so a line may legitimately be 2.5 kg.
// Round to hundredths rather than flooring — flooring silently turned every
// fractional quantity into a smaller whole one.
const normalizeQuantity = (value) => Math.round(normalizeNonNegativeNumber(value) * 100) / 100;

/** RFC 4122 v4, shared with every other idempotent write. */
const createSaleRequestId = () => newClientRequestId();

const saleDraftFingerprint = (tab) => {
    const items = Array.isArray(tab?.items) ? tab.items : [];
    const form = tab?.form ?? {};
    const summary = calculateSaleSummary(items, form);

    return JSON.stringify({
        branchId: String(form.branch_id ?? ''),
        paymentBranchId: String(form.payment_branch_id ?? form.branch_id ?? ''),
        generalOrder: Boolean(form.general_order),
        customerId: String(tab?.customer?.id ?? ''),
        automotiveId: String(form.automotive_id ?? ''),
        // Normalised so 12 / '12' / 12.0 hash alike — retyping the same reading
        // must not invalidate an open payment.
        automotiveOdometer:
            form.service_record?.odometer === '' || form.service_record?.odometer == null
                ? ''
                : String(Math.trunc(Number(form.service_record.odometer))),
        date: String(form.date ?? ''),
        note: String(form.notes ?? ''),
        discountType: String(form.discount_type ?? ''),
        discountValue: roundMoney(summary.discountValue),
        salesTaxPercentage: roundMoney(summary.salesTaxPercentage),
        fedTaxPercentage: roundMoney(summary.fedTaxPercentage),
        extraTaxPercentage: roundMoney(summary.extraTaxPercentage),
        // The same basket for a filer and a non-filer is a different total, so
        // it must not reuse an open payment's request id.
        advanceTaxRate: roundMoney(tab?.customer?.advance_tax?.rate ?? 0),
        items: items.map((item) => [
            String(item.id ?? ''),
            // A damaged unit is one report per unit, so two reports of the same
            // variant are two different lines and must not share a fingerprint.
            String(item.damaged_item_id ?? ''),
            normalizeQuantity(item.qty),
            roundMoney(item.rate),
            String(item.discount_type === 'percent' ? 'percent' : 'rs'),
            roundMoney(item.discount ?? 0),
        ]),
    });
};

/**
 * Was the optional Job details panel actually used?
 *
 * An empty object must not be sent: the server rejects a service_record without
 * an automotive and would otherwise create an empty job card on every checkout.
 */
const hasServiceRecordInput = (record) => {
    if (!record || typeof record !== 'object') return false;

    return Object.entries(record).some(([key, value]) => {
        if (key === 'recommendations' || key === 'resolved_recommendation_ids') {
            return Array.isArray(value) && value.length > 0;
        }
        return value !== '' && value !== null && value !== undefined;
    });
};

/** Strip blanks so the request carries only what the cashier filled in. */
const compactServiceRecord = (record) => {
    const out = {};

    Object.entries(record ?? {}).forEach(([key, value]) => {
        if (key === 'recommendations') {
            const items = (Array.isArray(value) ? value : []).filter((item) => String(item?.description ?? '').trim() !== '');
            if (items.length) out.recommendations = items;
            return;
        }
        if (key === 'resolved_recommendation_ids') {
            const ids = (Array.isArray(value) ? value : []).map(Number).filter(Boolean);
            if (ids.length) out.resolved_recommendation_ids = ids;
            return;
        }
        if (value !== '' && value !== null && value !== undefined) out[key] = value;
    });

    return out;
};

/**
 * The buyer's standing discount (`customers.discount_percentage`), as a percent.
 *
 * Zero for a walk-in, and zero for a cached customer row written before the
 * column reached the offline payload — restrictive rather than wrong, and the
 * cashier can still type the rate in.
 */
const customerDiscountPercent = (tab) => {
    const percent = Number(tab?.customer?.discount_percentage ?? 0);
    if (!Number.isFinite(percent) || percent <= 0) return 0;
    return Math.min(percent, 100);
};

/**
 * Decide the automatic discount on one cart line.
 *
 * Two defaults compete for the Discount box: the figure the variant carries in
 * the catalogue (`branch_variants` / `product_variants`, kept here as
 * `base_discount`) and the customer's standing percentage. The buyer keeps
 * whichever is worth more on this line — compared in cents, because a rupee
 * figure and a percentage can only be weighed against each other at the
 * quantity actually being sold. That is also why this is re-run on every
 * quantity change: 200 PKR beats 10% on one unit and loses on three.
 *
 * A line the cashier has typed into is theirs from then on. `discount_source`
 * marks that the same way `furtherTaxAutoFilled` does for the header rate: this
 * is a default, not a lock.
 */
const withAutoLineDiscount = (item, customerPercent) => {
    if (item?.discount_source === 'manual') return item;
    // A damaged unit is already discounted to its damage price; the customer's
    // standing percentage must not stack on top of it automatically.
    if (item?.is_damaged_sale) return item;

    // A draft persisted before this existed carries no baseline, so whatever
    // discount it already holds is the catalogue figure by definition.
    const baseType = (item?.base_discount_type ?? item?.discount_type) === 'percent' ? 'percent' : 'rs';
    const baseValue = Number(item?.base_discount ?? item?.discount ?? 0) || 0;

    const catalogueCents = lineDiscountCents({
        ...item,
        discount: baseValue,
        discount_type: baseType,
    });
    const customerCents = customerPercent > 0 ? percentOf(lineGrossCents(item), toBasisPoints(customerPercent)) : 0;

    const useCustomer = customerCents > catalogueCents;

    return {
        ...item,
        base_discount: baseValue,
        base_discount_type: baseType,
        discount: useCustomer ? customerPercent : baseValue,
        discount_type: useCustomer ? 'percent' : baseType,
        discount_source: useCustomer ? 'customer' : 'catalogue',
    };
};

const isGeneralOrderBranch = (branchId) => branchId === null || branchId === undefined || branchId === '' || branchId === 'general';

const isMultiBranchAddonEnabled = () => {
    try {
        return Boolean(useConfigurationStore().isAddonEnabled('multi_branch_fulfillment'));
    } catch {
        return false;
    }
};

const isGeneralOrderTab = (tab) => {
    // Company-wide stock search / multi-branch cart mode. Initiation branch
    // (form.branch_id) stays set separately so the front office is always recorded.
    if (Boolean(tab?.form?.general_order) || tab?.form?.branch_id === 'general') {
        return isMultiBranchAddonEnabled();
    }
    return false;
};

const beginCustomerRequest = (tabId) => {
    const requestId = ++customerRequestSequence;
    latestCustomerRequestByTab.set(tabId, requestId);
    return requestId;
};

const invalidateCustomerRequest = (tabId) => {
    latestCustomerRequestByTab.set(tabId, ++customerRequestSequence);
};

const isLatestCustomerRequest = (tabId, requestId) => latestCustomerRequestByTab.get(tabId) === requestId;

const queuePersist = (fn) => {
    const generation = persistGeneration;
    clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
        persistTimer = null;
        persistChain = persistChain
            .then(() => (generation === persistGeneration ? fn() : undefined))
            .catch((err) => console.error('IDB persist error:', err));
    }, 250);
};

const snapshotStore = (store) => ({
    // Pinia only unwraps the root with toRaw(); nested tabs/items can still be
    // Vue proxies, which native structuredClone rejects with DataCloneError.
    // POS draft state is JSON-shaped, so JSON serialization is the reliable
    // deep de-proxy operation here.
    tabs: JSON.parse(JSON.stringify(toRaw(store.tabs))),
    activeTabId: store.activeTabId,
    tabCounter: store.tabCounter,
});

/**
 * The till's one search box, answered from the offline cache.
 *
 * Substring rather than the server's prefix match: this is a scan of a local
 * table, and a superset of what the server would return can only ever help the
 * cashier — it never hides a customer the network leg would have found. Phone
 * matching is unchanged from when this only searched phones.
 */
const findCachedCustomers = async (term) => {
    if (!offlineDB.customers) return [];
    const query = String(term ?? '')
        .trim()
        .toLowerCase();
    if (!query) return [];
    const contains = (value) =>
        String(value ?? '')
            .toLowerCase()
            .includes(query);

    // Indexed answers first. What a cashier types at the customer box is nearly
    // always a whole phone number or an id, and both of those are prefix matches
    // from position zero — which the `phone` index and the primary key answer
    // without touching another row. The substring scan below is a full cursor
    // walk of every cached customer, and at twenty-five thousand rows it was
    // costing a fifth of a second on every Enter.
    const byIndex = new Map();
    const remember = (rows) => {
        for (const row of rows) if (row?.id != null) byIndex.set(String(row.id), row);
    };

    if (/^\d+$/.test(query)) {
        remember(await offlineDB.customers.where('phone').startsWith(query).limit(20).toArray());
        const byId = await offlineDB.customers.get(Number(query));
        if (byId) remember([byId]);
        if (byIndex.size >= 20) return [...byIndex.values()].slice(0, 20);
    }
    remember(await offlineDB.customers.where('name').startsWithIgnoreCase(query).limit(20).toArray());
    if (byIndex.size >= 20) return [...byIndex.values()].slice(0, 20);

    // Fallback: a mid-string match — a surname, part of a company name, an
    // internal code fragment. No index can serve it, so it still scans, but only
    // when the cheap paths came back short.
    const scanned = await offlineDB.customers
        .filter(
            (customer) =>
                contains(customer?.phone) || contains(customer?.name) || contains(customer?.internal_code) || String(customer?.id ?? '') === query,
        )
        .limit(20)
        .toArray();
    remember(scanned);

    return [...byIndex.values()].slice(0, 20);
};

const lookupCustomers = async (term) => {
    if (canMutateOnline()) {
        try {
            const response = await apiClient.post('/customer/fetchCustomer', { search: term }, { offlinePolicy: 'read' });
            return {
                customers: response.data?.data || [],
                authoritative: true,
            };
        } catch (error) {
            const cached = await findCachedCustomers(term);
            if (cached.length) {
                return { customers: cached, authoritative: false };
            }
            throw error;
        }
    }

    return {
        customers: await findCachedCustomers(term),
        authoritative: false,
    };
};

/**
 * Does this term look like the phone number of a customer who does not exist yet?
 *
 * Only then is offering to register one the right answer to an empty result: a
 * name, an internal code or an id typed at the till is a lookup that missed, and
 * prefilling the registration form's phone field with "Ali Khan" is not a
 * customer record anybody wants.
 */
const isPhoneTerm = (term) => /^\d{11}$/.test(String(term ?? '').trim());

/** What to show on the selected-customer pill for a buyer found by name or code. */
const customerPillLabel = (customer) => customer?.phone || customer?.name || customer?.internal_code || `CUS-${customer?.id ?? ''}`;

export const useSaleStore = defineStore('sale', {
    state: () => ({
        tabs: {},
        activeTabId: null,
        _skipNextSync: false,
        tabCounter: 0,
        paymentModal: false,
        paymentTabId: null,

        livePayment: {
            method: 'cash',
            amount: '',
            transaction_id: '',
            selectedAccount: null,
            activeModalTab: 'payment',
            creditChangeToWallet: false,
            walletCreditAmount: 0,
        },
        liveDelivery: {
            name: '',
            phone: '',
            address: '',
        },
        availableBranches: [],

        payment: {
            amount: 0,
            method: 'cash',
        },

        saleSubmitting: false,
        lastCreatedSaleId: null,

        // Set when the server refuses a sale on credit grounds. The POS reads the
        // figures out of here to reopen its approval dialog, then clears it.
        creditLimitRejection: null,
    }),

    getters: {
        currentTab: (state) => state.tabs[state.activeTabId] || null,
        form: (state) => state.tabs[state.activeTabId]?.form || {},
        items: (state) => state.tabs[state.activeTabId]?.items || [],
        customer: (state) => state.tabs[state.activeTabId]?.customer || {},
        selectedCustomerPill: (state) => state.tabs[state.activeTabId]?.selectedCustomerPill || '',
        matchingCustomers: (state) => state.tabs[state.activeTabId]?.matchingCustomers || [],
        showPhoneDropdown: (state) => state.tabs[state.activeTabId]?.showPhoneDropdown || false,
        showRegisterModal: (state) => state.tabs[state.activeTabId]?.showRegisterModal || false,
        newCustomerPhone: (state) => state.tabs[state.activeTabId]?.newCustomerPhone || '',
        showDeleteModal: (state) => state.tabs[state.activeTabId]?.showDeleteModal || false,
        deleteIndex: (state) => state.tabs[state.activeTabId]?.deleteIndex ?? null,
        checkedPhoneNumbers: (state) => state.tabs[state.activeTabId]?.checkedPhoneNumbers || [],
        isSubmitting: (state) => state.tabs[state.activeTabId]?.isSubmitting || false,
        searchTerm: (state) => state.tabs[state.activeTabId]?.searchTerm || '',
        apiItems: (state) => state.tabs[state.activeTabId]?.apiItems || [],
        showProductDropdown: (state) => state.tabs[state.activeTabId]?.showProductDropdown || false,

        grossAmount(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, tab.form).grossAmount;
        },
        totalDiscountAmount(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, tab.form).discountAmount;
        },
        taxAmount(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, tab.form).taxAmount;
        },
        netAmount(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, tab.form).netAmount;
        },
        finalTotal(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab) return 0;
            return calculateSaleSummary(tab.items, tab.form).finalTotal;
        },
        cityCountry(state) {
            const tab = state.tabs[state.activeTabId];
            if (!tab || (!tab.customer.city && !tab.customer.country)) return '—';
            return `${tab.customer.city ?? ''} | ${tab.customer.country ?? ''}`;
        },
        allTabIds: (state) => Object.keys(state.tabs),
    },

    actions: {
        // ── IDB persistence ───────────────────────────────────────

        async loadFromIDB() {
            const generation = persistGeneration;
            try {
                const saved = await loadStoreState();
                if (generation !== persistGeneration) return;
                if (saved && saved.tabs) {
                    // A persisted request cannot still be in flight after a
                    // browser restart. Keep its payload for verification/retry,
                    // but never restore the transient submission lock.
                    this.tabs = Object.fromEntries(
                        Object.entries(saved.tabs).map(([id, tab]) => [
                            id,
                            {
                                ...tab,
                                isSubmitting: false,
                                items: Array.isArray(tab?.items) ? tab.items.map(migrateRestoredItem) : tab?.items,
                            },
                        ]),
                    );
                    this.activeTabId = saved.activeTabId ?? null;
                    this.tabCounter = saved.tabCounter ?? 0;

                    // Rebuild the aggregate reservation counters from the
                    // restored carts. A crash can otherwise leave quantities
                    // and branch_products.reserved_qty out of sync.
                    reservationGeneration++;
                    await resetAllReservations();
                    const branchIds = new Set(
                        Object.values(this.tabs)
                            .map((tab) => tab?.form?.branch_id)
                            .filter((branchId) => branchId !== null && branchId !== undefined && branchId !== ''),
                    );
                    for (const branchId of branchIds) {
                        await this._refreshAllTabsMaxQty(branchId);
                    }
                }
            } catch (err) {
                console.error('IDB load error:', err);
            }
        },

        persistToIDB() {
            const cacheWriteSnapshot = captureOfflineCacheWriteEpoch({ requireOwner: true });
            const snapshot = snapshotStore(this);
            queuePersist(async () => {
                await saveStoreState(snapshot, cacheWriteSnapshot);
            });
        },

        async flushPersistToIDB() {
            clearTimeout(persistTimer);
            persistTimer = null;
            const generation = persistGeneration;
            const cacheWriteSnapshot = captureOfflineCacheWriteEpoch({ requireOwner: true });
            const snapshot = snapshotStore(this);
            persistChain = persistChain
                .then(() => (generation === persistGeneration ? saveStoreState(snapshot, cacheWriteSnapshot) : undefined))
                .catch((err) => console.error('IDB persist error:', err));
            await persistChain;
        },

        getFreshTabData() {
            return {
                customer: {},
                selectedCustomerPill: '',
                form: {
                    id: '',
                    customer_id: '',
                    phone: '',
                    name: '',
                    discount: 0,
                    discount_type: 'percent',
                    salesTax: 0,
                    notes: '',
                    branch_id: null,
                    payment_branch_id: null,
                    // When multi-branch is on, fresh carts search company stock by default
                    // while initiation branch_id is filled separately on the POS.
                    general_order: isMultiBranchAddonEnabled(),
                    date: todayLocal(),
                    // Automotive addon. Absent on drafts persisted before the
                    // upgrade, so every read must tolerate undefined.
                    automotive_id: '',
                    automotive_label: '',
                    service_record: null,
                },
                items: [],
                searchTerm: '',
                apiItems: [],
                showProductDropdown: false,
                matchingCustomers: [],
                showPhoneDropdown: false,
                showRegisterModal: false,
                newCustomerPhone: '',
                showDeleteModal: false,
                deleteIndex: null,
                checkedPhoneNumbers: [],
                isSubmitting: false,

                pendingSalePayload: null,
            };
        },

        _setTab(tabId, data) {
            this.tabs[tabId] = data;
            this.persistToIDB();
        },

        initTab(tabId) {
            if (!this.tabs[tabId]) {
                this.tabs[tabId] = this.getFreshTabData();
            }
            this.activeTabId = tabId;
            this.persistToIDB();
        },

        setActiveTab(tabId) {
            if (this.tabs[tabId]) {
                this.activeTabId = tabId;
                this.persistToIDB();
            }
        },

        createNewTab() {
            this.tabCounter++;
            const tabId = `tab-${this.tabCounter}`;
            this._setTab(tabId, this.getFreshTabData());
            this.activeTabId = tabId;
            this.persistToIDB();
            return tabId;
        },

        switchTab(tabId) {
            if (!this.tabs[tabId]) return;
            this.activeTabId = tabId;
            this.persistToIDB();
        },

        async removeTab(tabId) {
            const branchId = this.tabs[tabId]?.form?.branch_id;
            invalidateCustomerRequest(tabId);
            delete this.tabs[tabId];
            if (this.paymentTabId === tabId) {
                this.paymentTabId = null;
                this.paymentModal = false;
            }
            if (this.activeTabId === tabId) {
                const remaining = Object.keys(this.tabs);
                this.activeTabId = remaining.length > 0 ? remaining[0] : null;
            }
            reservationGeneration++;
            this.persistToIDB();
            if (branchId !== null && branchId !== undefined && branchId !== '') {
                await this._refreshAllTabsMaxQty(branchId);
            }
        },

        async clearTab(tabId) {
            if (this.tabs[tabId]) {
                const branchId = this.tabs[tabId]?.form?.branch_id;
                invalidateCustomerRequest(tabId);
                this._setTab(tabId, this.getFreshTabData());
                if (this.paymentTabId === tabId) {
                    this.paymentTabId = null;
                    this.paymentModal = false;
                }
                reservationGeneration++;
                if (branchId !== null && branchId !== undefined && branchId !== '') {
                    await this._refreshAllTabsMaxQty(branchId);
                }
            }
        },

        async clearTabItems(tabId, reservationBranchId = this.tabs[tabId]?.form?.branch_id) {
            const tab = this.tabs[tabId];
            if (!tab) return;
            const currentBranchId = tab.form?.branch_id;
            this._setTab(tabId, {
                ...tab,
                items: [],
                pendingSalePayload: null,
            });
            reservationGeneration++;
            const affectedBranches = new Set(
                [reservationBranchId, currentBranchId].filter((branchId) => branchId !== null && branchId !== undefined && branchId !== ''),
            );
            for (const branchId of affectedBranches) {
                await this._refreshAllTabsMaxQty(branchId);
            }
        },

        /**
         * Drop identity-bound Pinia state without touching shared IndexedDB.
         * Used by other tabs when one tab owns the database cleanup.
         */
        resetLocalSessionState() {
            persistGeneration++;
            reservationGeneration++;
            latestCustomerRequestByTab.clear();
            clearTimeout(persistTimer);
            persistTimer = null;
            this.$reset();
        },

        async clearAll() {
            this.resetLocalSessionState();
            await reservationChain.catch(() => {});
            await persistChain;
            await clearStoreState();
        },

        async resetCurrentTab() {
            const tabId = this.activeTabId;
            if (!this.tabs[tabId]) return;
            await this.clearTab(tabId);
        },

        updateFormField(field, value) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            const previousBranchId = tab.form?.branch_id;
            const branchChanged = field === 'branch_id' && String(previousBranchId ?? '') !== String(value ?? '');
            if (branchChanged && !canMutateOnline()) {
                useToast().displayToast({
                    message: 'Reconnect before changing the sale branch.',
                    variant: 'warning',
                });
                return;
            }
            if (field === 'phone' && value !== tab.form?.phone) {
                invalidateCustomerRequest(tabId);
            }

            const nextForm = { ...tab.form, [field]: value };
            const previousGeneralOrder = Boolean(tab.form?.general_order);
            let stockScopeChanged = false;

            // Initiation / front-office branch — always a real branch id.
            // The cash till follows it.
            if (field === 'branch_id') {
                if (isGeneralOrderBranch(value)) {
                    useToast().displayToast({
                        message: 'Select the front-office branch where this order is initiated.',
                        variant: 'warning',
                    });
                    return;
                }
                nextForm.branch_id = Number(value);
                nextForm.payment_branch_id = nextForm.branch_id;
            }

            // Company-wide stock search scope (independent of initiation branch).
            if (field === 'general_order') {
                if (value && !isMultiBranchAddonEnabled()) {
                    nextForm.general_order = false;
                } else {
                    nextForm.general_order = Boolean(value);
                }
                stockScopeChanged = previousGeneralOrder !== Boolean(nextForm.general_order);
                nextForm.payment_branch_id = nextForm.branch_id ?? null;
            }

            // Only a change that can invalidate a line already on the cart may
            // discard it. Widening the stock search to the company pool cannot:
            // every branch that could source a line before can still source it.
            // Narrowing it back to the order-from branch can, because lines may
            // have been picked from stock this branch does not hold.
            const narrowedStockScope = stockScopeChanged && !nextForm.general_order;
            // A company-wide cart is not sourced from the order-from branch at
            // all — that branch is the front office — so moving it leaves every
            // line valid and the cart stands.
            const branchChangeInvalidates = branchChanged && !nextForm.general_order;
            const shouldClearCart = (branchChangeInvalidates || narrowedStockScope) && tab.items.length > 0;

            this._setTab(tabId, {
                ...tab,
                form: nextForm,
                ...(shouldClearCart
                    ? {
                          items: [],
                          pendingSalePayload: null,
                      }
                    : {}),
            });

            if (branchChanged || stockScopeChanged) {
                reservationGeneration++;
                if (shouldClearCart) {
                    useToast().displayToast({
                        message: branchChanged ? 'Order-from branch changed — cart cleared.' : 'Stock search scope changed — cart cleared.',
                        variant: 'warning',
                    });
                }
                if (!isGeneralOrderBranch(previousBranchId) && !previousGeneralOrder) {
                    void this._refreshAllTabsMaxQty(previousBranchId).catch(() => {});
                }
                if (!isGeneralOrderBranch(nextForm.branch_id) && !nextForm.general_order) {
                    void this._refreshAllTabsMaxQty(nextForm.branch_id).catch(() => {});
                }
            }
        },

        updateTabData(field, value) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            this._setTab(tabId, { ...tab, [field]: value });
            if (field === 'items') {
                reservationGeneration++;
                const branchId = tab.form?.branch_id;
                if (!isGeneralOrderBranch(branchId)) {
                    void this._refreshAllTabsMaxQty(branchId).catch(() => {});
                }
            }
        },

        /**
         * Push the buyer's standing discount onto every line it still owns.
         *
         * Run whenever the attached customer changes, so detaching one hands the
         * lines back to their catalogue figures rather than leaving the departed
         * buyer's rate priced into the sale.
         */
        applyCustomerDiscount(tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab?.items?.length) return;

            const percent = customerDiscountPercent(tab);
            const items = tab.items.map((item) => withAutoLineDiscount(item, percent));
            const changed = items.some((item, index) => {
                const before = tab.items[index];
                return (
                    Number(before.discount ?? 0) !== Number(item.discount ?? 0) ||
                    before.discount_type !== item.discount_type ||
                    before.discount_source !== item.discount_source ||
                    before.base_discount !== item.base_discount ||
                    before.base_discount_type !== item.base_discount_type
                );
            });
            if (!changed) return;

            this._setTab(tabId, { ...tab, items });
        },

        clearCustomerSelection() {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            invalidateCustomerRequest(tabId);
            this._setTab(tabId, {
                ...tab,
                selectedCustomerPill: '',
                customer: {},
                checkedPhoneNumbers: [],
                // The automotive belongs to the customer, so it can never outlive them.
                form: {
                    ...tab.form,
                    id: '',
                    phone: '',
                    name: '',
                    customer_id: '',
                    automotive_id: '',
                    automotive_label: '',
                    service_record: null,
                },
            });
            this.applyCustomerDiscount(tabId);
        },

        /**
         * Resolve whatever the cashier typed into the till's customer box.
         *
         * The term may be a phone number, a name, an internal code or the
         * customer id — the server ORs across all four. Registration is still
         * only offered for a phone number, since that is the one shape the
         * register modal can prefill from.
         */
        async searchAndShowDropdown(term) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            const requestId = beginCustomerRequest(tabId);
            try {
                const { customers, authoritative } = await lookupCustomers(term);
                if (!isLatestCustomerRequest(tabId, requestId) || !this.tabs[tabId] || String(this.tabs[tabId].form?.phone ?? '') !== String(term)) {
                    return;
                }
                const currentTab = this.tabs[tabId];
                if (customers.length > 0) {
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: customers,
                        showPhoneDropdown: true,
                    });
                } else if (authoritative && isPhoneTerm(term)) {
                    const newChecked = [...currentTab.checkedPhoneNumbers];
                    if (!newChecked.includes(term)) newChecked.push(term);
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: [],
                        showPhoneDropdown: false,
                        checkedPhoneNumbers: newChecked,
                        newCustomerPhone: term,
                        showRegisterModal: true,
                    });
                } else if (authoritative) {
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: [],
                        showPhoneDropdown: false,
                    });
                    useToast().displayToast({
                        message: `No customer matches "${term}". Search by phone to register a new one.`,
                        variant: 'warning',
                    });
                } else {
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: [],
                        showPhoneDropdown: false,
                    });
                    useToast().displayToast({
                        message: 'Customer is not in the offline cache. Reconnect to search or register.',
                        variant: 'warning',
                    });
                }
            } catch (err) {
                console.error('searchAndShowDropdown error:', err);
                // A failed lookup is not evidence that the customer does not
                // exist. Do not offer registration based on a network error.
            }
        },

        async onPhoneInput(phone) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            invalidateCustomerRequest(tabId);
            this._setTab(tabId, {
                ...tab,
                form: { ...tab.form, phone },
                matchingCustomers: [],
                showPhoneDropdown: false,
            });
            if (phone.length < 3) return;
            if (phone.length >= 3 && phone.length < 11) {
                const requestId = beginCustomerRequest(tabId);
                try {
                    const { customers } = await lookupCustomers(phone);
                    if (
                        !isLatestCustomerRequest(tabId, requestId) ||
                        !this.tabs[tabId] ||
                        String(this.tabs[tabId].form?.phone ?? '') !== String(phone)
                    ) {
                        return;
                    }
                    const currentTab = this.tabs[tabId];
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: customers,
                        showPhoneDropdown: customers.length > 0,
                    });
                } catch (err) {
                    console.error('Phone search error:', err);
                }
                return;
            }
            if (phone.length === 11) {
                await this.resolvePhone(phone, tabId);
            }
        },

        async resolvePhone(phone, tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab) return;
            if (tab.selectedCustomerPill === phone) return;
            if (tab.checkedPhoneNumbers.includes(phone)) return;
            const requestId = beginCustomerRequest(tabId);
            try {
                const { customers, authoritative } = await lookupCustomers(phone);
                if (!isLatestCustomerRequest(tabId, requestId) || !this.tabs[tabId] || String(this.tabs[tabId].form?.phone ?? '') !== String(phone)) {
                    return;
                }
                const currentTab = this.tabs[tabId];
                const newChecked = [...(currentTab.checkedPhoneNumbers || [])];
                if (!newChecked.includes(phone)) newChecked.push(phone);
                if (customers.length > 0) {
                    this.selectCustomerFromDropdown(customers[0], tabId);
                } else if (authoritative) {
                    this._setTab(tabId, {
                        ...currentTab,
                        checkedPhoneNumbers: newChecked,
                        newCustomerPhone: phone,
                        showRegisterModal: true,
                        showPhoneDropdown: false,
                        matchingCustomers: [],
                    });
                } else {
                    this._setTab(tabId, {
                        ...currentTab,
                        matchingCustomers: [],
                        showPhoneDropdown: false,
                    });
                    useToast().displayToast({
                        message: 'Customer is not in the offline cache. Reconnect to search or register.',
                        variant: 'warning',
                    });
                }
            } catch (err) {
                console.error('resolvePhone error:', err);
            }
        },

        selectCustomerFromDropdown(cust, tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab) return;
            invalidateCustomerRequest(tabId);
            const newChecked = [...tab.checkedPhoneNumbers];
            if (!newChecked.includes(cust.phone)) newChecked.push(cust.phone);
            this._setTab(tabId, {
                ...tab,
                customer: JSON.parse(JSON.stringify(cust)),
                form: {
                    ...tab.form,
                    id: cust.id,
                    customer_id: cust.id || null,
                    phone: cust.phone,
                    name: cust.name,
                    // A previous customer's automotive must never survive a
                    // customer switch.
                    automotive_id: '',
                    automotive_label: '',
                    service_record: null,
                },
                // Falls back to name / code / id: a customer found by name may
                // have no phone at all, and an empty pill reads as "nobody
                // attached" while the sale is in fact bound to them.
                selectedCustomerPill: customerPillLabel(cust),
                matchingCustomers: [],
                showPhoneDropdown: false,
                checkedPhoneNumbers: newChecked,
            });
            this.applyCustomerDiscount(tabId);
        },

        closeRegisterModal(tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab) return;
            invalidateCustomerRequest(tabId);
            this._setTab(tabId, { ...tab, showRegisterModal: false, newCustomerPhone: '' });
        },

        /**
         * The register modal hands back the row it saved. Re-fetch by its id
         * rather than by the phone typed at the till: the full form lets the
         * operator correct the number on the way through, and an id lookup also
         * finds a customer saved with no phone at all.
         */
        async handleCustomerCreated(customer = null) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            const lookup = customer?.id ? { id: customer.id } : { phone: customer?.phone || tab.newCustomerPhone };
            const requestId = beginCustomerRequest(tabId);
            await new Promise((r) => setTimeout(r, 500));
            if (!isLatestCustomerRequest(tabId, requestId) || !this.tabs[tabId]) return;
            try {
                const res = await apiClient.post('/customer/fetchCustomer', lookup, { offlinePolicy: 'read' });
                if (!isLatestCustomerRequest(tabId, requestId) || !this.tabs[tabId]) return;
                if (res.data?.success && res.data.data?.length) {
                    const newCust = res.data.data[0];
                    const currentTab = this.tabs[tabId];
                    const newChecked = [...currentTab.checkedPhoneNumbers];
                    if (!newChecked.includes(newCust.phone)) newChecked.push(newCust.phone);
                    this._setTab(tabId, {
                        ...currentTab,
                        customer: JSON.parse(JSON.stringify(newCust)),
                        form: {
                            ...currentTab.form,
                            id: newCust.id,
                            customer_id: newCust.id || null,
                            phone: newCust.phone,
                            name: newCust.name,
                            // A brand-new customer owns nothing yet.
                            automotive_id: '',
                            automotive_label: '',
                            service_record: null,
                        },
                        selectedCustomerPill: customerPillLabel(newCust),
                        checkedPhoneNumbers: newChecked,
                        showRegisterModal: false,
                        newCustomerPhone: '',
                    });
                    this.applyCustomerDiscount(tabId);
                }
            } catch (err) {
                console.error('handleCustomerCreated error:', err);
                useToast().displayToast({
                    message: 'Customer created but failed to load. Please search manually',
                    variant: 'error',
                });
            }
            if (isLatestCustomerRequest(tabId, requestId)) {
                this.closeRegisterModal(tabId);
            }
        },

        async addItemToSale(product) {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;

            const branchId = tab.form.branch_id;
            const isGeneral = isGeneralOrderTab(tab);
            const lineDiscountType = product.discount_type === 'percent' ? 'percent' : 'rs';
            const lineDiscount = Number(product.discount ?? 0) || 0;

            // A damaged report is a batch of units at an agreed price, sold in
            // as many checkouts as it takes to clear. `damaged_item_id` marks a
            // candidate row, the rate is the frozen damage price (tax-inclusive
            // like the catalogue), and the line may run up to whatever is left
            // of the report.
            //
            // Matched on the report id alone, never on the variant: a damaged
            // unit and a normal unit of the same product are two different
            // things and belong on two different lines.
            if (product.damaged_item_id != null) {
                if (tab.items.some((item) => String(item.damaged_item_id ?? '') === String(product.damaged_item_id))) return;
                const resolvedTaxPct = Number(product.tax_percentage ?? 0) || 0;
                // The ceiling is what the report has left; the line opens at one
                // unit. It used to open at the whole remainder, so picking a
                // 300-unit report put 300 units in the cart and one Enter sold
                // the entire batch.
                const remainingQty = Math.max(
                    0,
                    normalizeQuantity(product.remaining_quantity ?? product.maxQty ?? 1),
                );
                const openingQty = Math.min(normalizeQuantity(product.quantity ?? 1) || 1, remainingQty);
                const newItems = [
                    ...tab.items,
                    {
                        id: product.id,
                        name: product.name,
                        qty: openingQty,
                        rate: cartRateFrom(product, resolvedTaxPct),
                        // As above: already ex-tax, so the restore migration skips it.
                        rate_excludes_tax: true,
                        maxQty: remainingQty,
                        _lastQty: 0,
                        damaged_item_id: product.damaged_item_id,
                        is_damaged_sale: true,
                        // The report's remaining quantity is the sale cap, and it
                        // is capped again server-side under the report's row lock.
                        damage_qty: remainingQty,
                        damage_price: Number(product.damage_price ?? 0) || 0,
                        shelf_price: Number(product.shelf_price ?? 0) || 0,
                        tax_rate: product.tax_rate ?? 0,
                        tax_percentage: resolvedTaxPct,
                        tax_mode: product.tax_mode ?? 'inclusive',
                        // Same snapshot decisions the checkout freezes by.
                        is_third_schedule: Boolean(product.third_schedule),
                        mrp: Number(product.mrp ?? 0) > 0 ? Number(product.mrp) : null,
                        supply_type: product.supply_type ?? 'standard',
                        fed_excisable: Boolean(product.fed_excisable),
                        unit_abbr: product.unit_abbr || '',
                        // The unit of measure decides, exactly as it does for a
                        // catalogue line: a damaged 0.4 kg is as sellable as an
                        // undamaged one, and the server holds the same rule.
                        allows_fractional: Boolean(product.allows_fractional),
                        base_discount: 0,
                        base_discount_type: 'rs',
                        discount_source: 'catalogue',
                        discount: 0,
                        discount_type: 'rs',
                    },
                ];
                this._setTab(tabId, { ...tab, items: newItems });
                return;
            }

            // General / company orders have no single-branch reservation surface.
            if (isGeneral) {
                // `!item.damaged_item_id` on purpose: a damaged line happens to
                // carry the same variant id, and matching on that alone refused
                // to add the normal unit at all — so a cart could never hold a
                // damaged and an undamaged unit of the same product.
                if (tab.items.some((item) => !item.damaged_item_id && String(item.id) === String(product.id))) return;
                const newItems = [
                    ...tab.items,
                    {
                        id: product.id,
                        name: product.name,
                        qty: 1,
                        rate: cartRateFrom(product, Number(product.tax_percentage ?? 0) || 0),
                        // Marks the rate above as already converted, so a draft
                        // restored after a deploy is not converted a second time.
                        rate_excludes_tax: true,
                        maxQty: Number.isFinite(Number(product.maxQty)) ? Math.max(0, normalizeQuantity(product.maxQty)) : Number.MAX_SAFE_INTEGER,
                        _lastQty: 0,
                        tax_rate: product.tax_rate ?? 0,
                        tax_percentage: Number(product.tax_percentage ?? 0) || 0,
                        tax_mode: product.tax_mode ?? 'none',
                        // Decides which header taxes the quote may include.
                        // Unclassified reads as standard-rated, matching
                        // SaleLineSnapshot — restrictive rather than wrong.
                        is_third_schedule: Boolean(product.third_schedule),
                        // Printed retail price. A third-schedule line is taxed on
                        // this and not on what it sold for, so a discount must
                        // not shrink its tax — without it the quote taxes the
                        // discounted value and checkout rejects the basket.
                        mrp: Number(product.mrp ?? 0) > 0 ? Number(product.mrp) : null,
                        supply_type: product.supply_type ?? 'standard',
                        fed_excisable: Boolean(product.fed_excisable),
                        unit_abbr: product.unit_abbr || '',
                        allows_fractional: Boolean(product.allows_fractional),
                        // The catalogue figure is kept as the baseline the
                        // customer's standing rate is weighed against; validateQty
                        // below decides which of the two the line actually gets.
                        base_discount: lineDiscount,
                        base_discount_type: lineDiscountType,
                        discount_source: 'catalogue',
                        discount: lineDiscount,
                        discount_type: lineDiscountType,
                    },
                ];
                this._setTab(tabId, { ...tab, items: newItems });
                await this.validateQty(newItems.length - 1, tabId);
                return;
            }

            if (branchId === null || branchId === undefined || branchId === '') return;
            const key = `${branchId}-${product.id}`;
            const record = await offlineDB.branch_products.get(key);
            const currentTab = this.tabs[tabId];
            if (!currentTab || String(currentTab.form?.branch_id) !== String(branchId)) return;
            // As above: a damaged line of this variant is a different line.
            if (currentTab.items.some((item) => !item.damaged_item_id && String(item.id) === String(product.id))) return;

            const real = Number(record?.real_qty ?? 0);
            const reserved = Number(record?.reserved_qty ?? 0);
            const available = Math.max(real - reserved, 0);

            // Prefer product search payload; fall back to offline branch cache.
            const cachedType = record?.discount_type === 'percent' ? 'percent' : record?.discount_type === 'rs' ? 'rs' : null;
            const resolvedType =
                product.discount_type === 'percent' || product.discount_type === 'rs'
                    ? product.discount_type === 'percent'
                        ? 'percent'
                        : 'rs'
                    : cachedType || 'rs';
            const resolvedDiscount = Number.isFinite(Number(product.discount))
                ? Number(product.discount) || 0
                : Number(record?.discount_value ?? 0) || 0;
            const resolvedTaxPct = Number.isFinite(Number(product.tax_percentage))
                ? Number(product.tax_percentage) || 0
                : Number(record?.tax_percentage ?? 0) || 0;

            const newItems = [
                ...currentTab.items,
                {
                    id: product.id,
                    name: product.name,
                    qty: 1,
                    rate: cartRateFrom(product, resolvedTaxPct),
                    // As above: already ex-tax, so the restore migration skips it.
                    rate_excludes_tax: true,
                    maxQty: available,
                    _lastQty: 0,
                    tax_rate: product.tax_rate ?? 0,
                    tax_percentage: resolvedTaxPct,
                    tax_mode: product.tax_mode ?? 'none',
                    // Same search-payload-then-branch-cache order as unit_abbr
                    // below; a cache predating the fields reads as standard.
                    is_third_schedule: Boolean(product.third_schedule ?? record?.third_schedule),
                    // The basis that flag puts the tax on, same search-payload-
                    // then-branch-cache order. Null falls back to the line's own
                    // value, which is what the server does with a missing MRP.
                    mrp: Number(product.mrp ?? record?.mrp ?? 0) > 0 ? Number(product.mrp ?? record?.mrp) : null,
                    supply_type: product.supply_type ?? record?.supply_type ?? 'standard',
                    fed_excisable: Boolean(product.fed_excisable ?? record?.fed_excisable),
                    unit_abbr: product.unit_abbr || record?.unit_abbr || '',
                    // Same search-payload-then-branch-cache order as unit_abbr:
                    // a catalogue row cached before the field existed falls back
                    // to the branch row, which is refreshed on a 30s TTL.
                    allows_fractional: Boolean(product.allows_fractional ?? record?.allows_fractional),
                    base_discount: resolvedDiscount,
                    base_discount_type: resolvedType,
                    discount_source: 'catalogue',
                    discount: resolvedDiscount,
                    discount_type: resolvedType,
                },
            ];

            this._setTab(tabId, { ...currentTab, items: newItems });
            await this.validateQty(newItems.length - 1, tabId);
        },

        async validateQty(index, tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab) return;

            const item = tab.items[index];
            if (!item) return;

            const branchId = tab.form.branch_id;
            let qty = normalizeQuantity(item.qty);
            const maxQty = Number(item.maxQty);
            // Clamp to known availability for both single-branch and company-wide
            // carts. Company carts still skip local Dexie reservation below.
            if (Number.isFinite(maxQty) && maxQty >= 0) {
                qty = Math.min(qty, Math.floor(maxQty));
            }

            // Only the edited line is re-priced: whether the customer's standing
            // percentage beats the line's own rupee discount depends on the
            // quantity, and no other line's quantity moved.
            const customerPercent = customerDiscountPercent(tab);
            const items = tab.items.map((candidate, itemIndex) =>
                itemIndex === index ? withAutoLineDiscount({ ...candidate, qty }, customerPercent) : candidate,
            );
            this._setTab(tabId, { ...tab, items });
            reservationGeneration++;
            // General orders skip local single-branch reservation reconciliation.
            if (isGeneralOrderTab(tab) || isGeneralOrderBranch(branchId)) return;
            await this._refreshAllTabsMaxQty(branchId);
        },

        async resetReservations() {
            const branchId = this.tabs[this.activeTabId]?.form?.branch_id;
            await resetAllReservations(branchId);
            reservationGeneration++;
            const branchIds =
                branchId !== null && branchId !== undefined && branchId !== ''
                    ? [branchId]
                    : [
                          ...new Set(
                              Object.values(this.tabs)
                                  .map((tab) => tab?.form?.branch_id)
                                  .filter((id) => id !== null && id !== undefined && id !== ''),
                          ),
                      ];
            for (const id of branchIds) {
                await this._refreshAllTabsMaxQty(id);
            }
        },

        async removeItem(index, tabId = this.activeTabId) {
            const tab = this.tabs[tabId];
            if (!tab) return;

            const newItems = [...tab.items];
            newItems.splice(index, 1);
            this._setTab(tabId, { ...tab, items: newItems });
            reservationGeneration++;
            const branchId = tab.form?.branch_id;
            if (!isGeneralOrderBranch(branchId)) {
                await this._refreshAllTabsMaxQty(branchId);
            }
        },

        openDeleteModal(index) {
            const tab = this.tabs[this.activeTabId];
            if (!tab) return;
            this._setTab(this.activeTabId, { ...tab, deleteIndex: index, showDeleteModal: true });
        },

        async confirmDelete() {
            const tabId = this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab) return;
            if (tab.deleteIndex !== null) await this.removeItem(tab.deleteIndex, tabId);
            if (!this.tabs[tabId]) return;
            this._setTab(tabId, {
                ...this.tabs[tabId],
                showDeleteModal: false,
                deleteIndex: null,
            });
        },

        closeDeleteModal() {
            const tab = this.tabs[this.activeTabId];
            if (!tab) return;
            this._setTab(this.activeTabId, { ...tab, showDeleteModal: false, deleteIndex: null });
        },

        async _refreshAllTabsMaxQty(branchId) {
            if (branchId === null || branchId === undefined || branchId === '') return;
            const branchKey = String(branchId);

            const reconcile = async () => {
                // A cart may change while IndexedDB is yielding. Repeat until
                // both the records and Pinia snapshot represent one generation.
                for (let attempt = 0; attempt < 5; attempt++) {
                    const generationAtStart = reservationGeneration;
                    let itemPlans = [];

                    await offlineDB.transaction('rw', offlineDB.branch_products, async () => {
                        // Through the `branch_id` index, not a cursor scan.
                        // `.filter()` in Dexie walks and deserializes every row
                        // in the store, so a till reconciling its cart re-read
                        // every branch it had ever cached — on every add, remove
                        // and quantity commit. `anyOf` keeps the tolerance the
                        // predicate had: older schema versions stored branch_id
                        // as a number, newer ones as a string.
                        const records = await offlineDB.branch_products
                            .where('branch_id')
                            .anyOf([branchKey, Number(branchKey)])
                            .toArray();
                        const recordsByKey = new Map(records.map((record) => [String(record.key), record]));

                        const entriesByKey = new Map();
                        for (const [tabId, tab] of Object.entries(this.tabs)) {
                            if (String(tab?.form?.branch_id ?? '') !== branchKey) continue;
                            for (const [index, item] of (tab.items || []).entries()) {
                                const variantId = Number(item.id);
                                if (!Number.isSafeInteger(variantId) || variantId <= 0) continue;
                                const key = `${branchKey}-${variantId}`;
                                const entries = entriesByKey.get(key) || [];
                                entries.push({
                                    tabId,
                                    index,
                                    id: item.id,
                                    desiredQty: normalizeQuantity(item.qty),
                                    // A damaged line's sale cap is its report
                                    // quantity, not the branch stock level.
                                    damageQty: Number(item.damage_qty),
                                });
                                entriesByKey.set(key, entries);
                            }
                        }

                        itemPlans = [];
                        for (const [key, entries] of entriesByKey) {
                            const record = recordsByKey.get(key);
                            const realQty = normalizeQuantity(record?.real_qty);
                            let remainingQty = realQty;
                            let totalReserved = 0;

                            for (const entry of entries) {
                                entry.assignedQty = Math.min(entry.desiredQty, remainingQty);
                                remainingQty -= entry.assignedQty;
                                totalReserved += entry.assignedQty;
                            }

                            for (const entry of entries) {
                                itemPlans.push({
                                    ...entry,
                                    maxQty: Number.isFinite(entry.damageQty)
                                        ? Math.min(Math.max(realQty - (totalReserved - entry.assignedQty), 0), entry.damageQty)
                                        : Math.max(realQty - (totalReserved - entry.assignedQty), 0),
                                });
                            }

                            if (record) {
                                record.reserved_qty = totalReserved;
                            }
                        }

                        // Rows no longer represented by any cart must not retain
                        // stale reservations after tab/cart deletion.
                        for (const record of records) {
                            if (!entriesByKey.has(String(record.key))) {
                                record.reserved_qty = 0;
                            }
                        }

                        if (records.length) await offlineDB.branch_products.bulkPut(records);
                    });

                    const stateChanged =
                        generationAtStart !== reservationGeneration ||
                        itemPlans.some((plan) => {
                            const current = this.tabs[plan.tabId]?.items?.[plan.index];
                            return !current || String(current.id) !== String(plan.id) || normalizeQuantity(current.qty) !== plan.desiredQty;
                        });

                    if (stateChanged) continue;

                    const plansByTab = new Map();
                    for (const plan of itemPlans) {
                        const plans = plansByTab.get(plan.tabId) || new Map();
                        plans.set(plan.index, plan);
                        plansByTab.set(plan.tabId, plans);
                    }

                    for (const [tabId, plans] of plansByTab) {
                        const tab = this.tabs[tabId];
                        if (!tab || String(tab.form?.branch_id ?? '') !== branchKey) continue;
                        this.tabs[tabId] = {
                            ...tab,
                            items: tab.items.map((item, index) => {
                                const plan = plans.get(index);
                                return plan
                                    ? {
                                          ...item,
                                          qty: plan.assignedQty,
                                          _lastQty: plan.assignedQty,
                                          maxQty: plan.maxQty,
                                      }
                                    : item;
                            }),
                        };
                    }

                    await this.flushPersistToIDB();
                    return;
                }

                throw new Error(`Could not reconcile branch ${branchKey} reservations because carts kept changing.`);
            };

            const run = reservationChain.then(reconcile, reconcile);
            reservationChain = run.catch((err) => {
                console.error('Reservation reconciliation failed:', err);
            });
            return run;
        },

        async prepareSale() {
            const tabId = this.activeTabId;
            let tab = this.tabs[tabId];
            if (!tab) {
                return;
            }
            if (tab.isSubmitting) {
                return;
            }

            const branchId = tab.form.branch_id;
            const isGeneral = isGeneralOrderTab(tab);

            if (!tab.items.length) {
                useToast().displayToast({ message: 'Please add at least one product', variant: 'error' });
                return;
            }
            if (isGeneralOrderBranch(branchId)) {
                useToast().displayToast({
                    message: 'Please select the front-office branch for this order',
                    variant: 'error',
                });
                return;
            }
            if (isGeneral && !canMutateOnline()) {
                useToast().displayToast({
                    message: 'Company-wide stock search requires an internet connection.',
                    variant: 'warning',
                });
                return;
            }

            reservationGeneration++;
            if (!isGeneral) {
                await this._refreshAllTabsMaxQty(branchId);
            } else if (isMultiBranchAddonEnabled() && canMutateOnline() && tab.items?.length) {
                try {
                    const availRes = await apiClient.post('sale/availability', {
                        origin_branch_id: branchId,
                        allocation_mode: 'company',
                        product_variant_ids: tab.items.map((i) => Number(i.id)),
                    });
                    await this.applyServerAvailability(availRes.data?.data ?? availRes.data ?? {});
                } catch (availErr) {
                    console.warn('Aggregate availability refresh before payment failed.', availErr);
                }
            }
            tab = this.tabs[tabId];
            if (!tab || this.activeTabId !== tabId) return;

            const invalid = tab.items.some(
                (item) =>
                    !Number.isSafeInteger(Number(item.qty)) || Number(item.qty) <= 0 || !Number.isFinite(Number(item.rate)) || Number(item.rate) < 0,
            );
            if (invalid) {
                useToast().displayToast({ message: 'Items must have a valid whole quantity and price', variant: 'error' });
                return;
            }

            const items = JSON.parse(JSON.stringify(tab.items));
            const summary = calculateSaleSummary(items, tab.form);
            const draftFingerprint = saleDraftFingerprint({ ...tab, items });
            const existingPending = tab.pendingSalePayload;
            const clientRequestId =
                existingPending?.draftFingerprint === draftFingerprint && existingPending?.clientRequestId
                    ? existingPending.clientRequestId
                    : createSaleRequestId();

            let previewAllocations = null;
            if (isMultiBranchAddonEnabled() && canMutateOnline()) {
                try {
                    const previewRes = await apiClient.post('sale/allocation-preview', {
                        origin_branch_id: branchId,
                        allocation_mode: isGeneral ? 'company' : undefined,
                        items: items.map((i) => ({
                            product_variant_id: i.id,
                            quantity: i.qty,
                        })),
                    });
                    previewAllocations = previewRes.data?.data ?? null;
                    if (previewAllocations && previewAllocations.allocatable === false) {
                        const short = Array.isArray(previewAllocations.shortfall) ? previewAllocations.shortfall : [];
                        const detail = short
                            .slice(0, 3)
                            .map((s) => `#${s.product_variant_id}: need ${s.requested}, have ${s.available}`)
                            .join('; ');
                        useToast().displayToast({
                            message: detail ? `Insufficient stock across branches — ${detail}` : 'Insufficient stock across branches for this cart.',
                            variant: 'error',
                        });
                        return;
                    }
                } catch (error) {
                    const status = error?.response?.status;
                    useToast().displayToast({
                        message:
                            error.response?.data?.message ||
                            (status === 422
                                ? 'Fulfillment pool is misconfigured for this order.'
                                : 'Could not verify stock allocation. Check your connection and try again.'),
                        variant: 'error',
                    });
                    return;
                }
            }

            this._setTab(tabId, {
                ...tab,
                pendingSalePayload: {
                    items,
                    // Always persist initiation / front-office branch on the sale.
                    // The cash till is derived from it at submit time.
                    branchId,
                    companyStockSearch: isGeneral,
                    previewAllocations,
                    clientRequestId,
                    draftFingerprint,
                    // Delivery charges are collected later, inside PaymentModal,
                    // so this summary necessarily excludes them. submitSale()
                    // recomputes from this snapshot once delivery is known
                    // rather than adding it on afterwards — the server folds
                    // delivery into the same total, and anything derived from
                    // it (advance tax) must see the same base.
                    formSnapshot: { ...tab.form },
                    ...summary,
                },
            });

            this.paymentTabId = tabId;
            this.paymentModal = true;
        },

        setLivePayment(data) {
            this.livePayment = { ...this.livePayment, ...data };
        },
        setLiveDelivery(data) {
            this.liveDelivery = { ...this.liveDelivery, ...data };
        },
        resetLivePayment() {
            // Every key setLivePayment can write is cleared here. A key left out
            // survives the merge into the next sale, so the customer display
            // would still be showing the last buyer's account or change.
            this.livePayment = {
                method: 'cash',
                amount: '',
                transaction_id: '',
                selectedAccount: null,
                activeModalTab: 'payment',
                creditChangeToWallet: false,
                walletCreditAmount: 0,
            };
            this.liveDelivery = { name: '', phone: '', address: '' };
        },
        setAvailableBranches(branches) {
            this.availableBranches = branches;
        },
        /**
         * Merge server availability (409 STOCK_CONFLICT or /sale/availability)
         * into Dexie. Display / maxQty only — never used to authorise a sale.
         */
        async applyServerAvailability(payload = {}) {
            const availability = Array.isArray(payload.availability)
                ? payload.availability
                : Array.isArray(payload.data?.availability)
                  ? payload.data.availability
                  : [];
            if (availability.length === 0) return;

            await cacheVariantAvailability(availability);

            if (!offlineDB.branch_products) return;

            await offlineDB.transaction('rw', offlineDB.branch_products, async () => {
                for (const row of availability) {
                    const variantId = Number(row.product_variant_id ?? row.variant_id);
                    if (!Number.isSafeInteger(variantId) || variantId <= 0) continue;
                    const branches = Array.isArray(row.by_branch) ? row.by_branch : [];
                    for (const branchRow of branches) {
                        const branchId = Number(branchRow.branch_id);
                        const available = Math.max(0, Math.floor(Number(branchRow.available) || 0));
                        if (!Number.isSafeInteger(branchId) || branchId <= 0) continue;
                        const key = `${branchId}-${variantId}`;
                        const record = await offlineDB.branch_products.get(key);
                        if (!record) continue;
                        const reserved = Math.max(0, Math.floor(Number(record.reserved_qty) || 0));
                        record.real_qty = available + reserved;
                        await offlineDB.branch_products.put(record);
                    }
                }
            });

            const tabId = this.paymentTabId ?? this.activeTabId;
            const tab = this.tabs[tabId];
            if (!tab?.items?.length) return;

            const byVariant = new Map(
                availability.map((row) => [
                    Number(row.product_variant_id ?? row.variant_id),
                    Math.max(0, Math.floor(Number(row.aggregate_available) || 0)),
                ]),
            );
            const nextItems = tab.items.map((item) => {
                const aggregate = byVariant.get(Number(item.id));
                if (aggregate == null) return item;
                return { ...item, maxQty: aggregate };
            });
            this._setTab(tabId, { ...tab, items: nextItems });
        },
        async submitSale(paymentData) {
            const tabId = this.paymentTabId ?? this.activeTabId;
            let tab = this.tabs[tabId];
            let pending = tab?.pendingSalePayload;

            if (!pending) return;
            if (this.activeTabId !== tabId) {
                useToast().displayToast({
                    message: 'Return to the sale tab that opened this payment before submitting.',
                    variant: 'warning',
                });
                return;
            }

            if (!canMutateOnline()) {
                useToast().displayToast({
                    message: 'You are offline — changes are disabled until the connection returns.',
                    variant: 'warning',
                });
                const err = new Error('This action requires an internet connection.');
                err.code = 'ERR_OFFLINE_WRITE';
                throw err;
            }

            const { items, branchId } = pending;
            const isGeneral = Boolean(pending.companyStockSearch) || isGeneralOrderTab(tab);

            if (isGeneralOrderBranch(branchId)) {
                useToast().displayToast({
                    message: 'Please select the front-office branch for this order',
                    variant: 'error',
                });
                return;
            }

            if (!items?.length) {
                useToast().displayToast({ message: 'No items found', variant: 'error' });
                return;
            }

            const currentDraftFingerprint = saleDraftFingerprint(tab);
            if (pending.draftFingerprint && pending.draftFingerprint !== currentDraftFingerprint) {
                this.paymentModal = false;
                this.paymentTabId = null;
                useToast().displayToast({
                    message: 'The sale changed after payment opened. Review it and open payment again.',
                    variant: 'warning',
                });
                return;
            }

            if (!pending.clientRequestId || !pending.draftFingerprint) {
                pending = {
                    ...pending,
                    clientRequestId: pending.clientRequestId || createSaleRequestId(),
                    draftFingerprint: pending.draftFingerprint || currentDraftFingerprint,
                };
                tab = { ...tab, pendingSalePayload: pending };
                this._setTab(tabId, tab);
                await this.flushPersistToIDB();
            }

            const tenderedAmount = Number(paymentData?.amount);
            if (!Number.isFinite(tenderedAmount) || tenderedAmount < 0) {
                useToast().displayToast({ message: 'Enter a valid non-negative payment amount', variant: 'error' });
                const error = new Error('Invalid payment amount.');
                error.code = 'INVALID_PAYMENT_AMOUNT';
                throw error;
            }

            const deliveryCharges = roundMoney(paymentData?.delivery?.charges ?? 0);
            // Recompute rather than add delivery to the prepared total: the
            // server runs the whole sale through SaleFinancialCalculator with
            // delivery included, and going through the same function here keeps
            // the quoted total identical to the persisted one. Adding it
            // afterwards round-trips through fromCents/toCents a second time and
            // can drift a paisa, which the server rejects as a tampered total.
            // The buyer's advance-tax standing, as resolved by the server when
            // the customer was attached. Echoed back so the server can reject a
            // quote raised against a rate that has since changed, rather than
            // silently charging a total the cashier never displayed.
            const advanceTax = tab.customer?.advance_tax ?? null;
            const advanceTaxRate = Number(advanceTax?.rate ?? 0) || 0;

            const settlement = calculateSaleSummary(items, {
                ...(pending.formSnapshot ?? {}),
                delivery_charges: deliveryCharges,
                advance_tax_rate: advanceTaxRate,
            });
            const grandTotal = settlement.finalTotal;
            const settledAmount = roundMoney(Math.min(tenderedAmount, grandTotal));
            const paymentStatus = grandTotal > 0 && settledAmount >= grandTotal ? 'paid' : settledAmount > 0 ? 'partially_paid' : 'unpaid';

            const isCash = paymentData?.method === 'cash';
            const cashExcess = isCash ? roundMoney(Math.max(tenderedAmount - grandTotal, 0)) : 0;
            const mayCreditChange = isCash && Boolean(paymentData?.credit_change_to_wallet) && Boolean(tab.customer?.id);
            const walletChange = mayCreditChange ? roundMoney(Math.min(normalizeNonNegativeNumber(paymentData?.change_amount), cashExcess)) : 0;
            const cashChange = isCash ? roundMoney(Math.max(cashExcess - walletChange, 0)) : 0;

            this._setTab(tabId, { ...this.tabs[tabId], isSubmitting: true });
            await this.flushPersistToIDB();

            const payload = {
                client_request_id: pending.clientRequestId,
                // Front-office / initiation branch — always stored on the sale.
                branch_id: branchId,
                // Cash till always follows the order-from branch.
                payment_branch_id: branchId,
                customer_id: tab.customer.id ?? null,
                date: tab.form.date,
                note: tab.form.notes ?? null,

                // Every derived total comes from `settlement`, the one summary
                // that knows the delivery charge. Mixing in the pre-delivery
                // `pending` figures is what let the two disagree.
                main_discount_type: tab.form.discount_type,
                main_discount_value: settlement.discountValue,
                sales_tax_percentage: settlement.salesTaxPercentage,

                gross_amount: settlement.grossAmount,
                total_discount_amount: settlement.discountAmount,
                taxable_amount: settlement.taxableAmount,
                tax_amount: settlement.taxAmount,
                net_amount: settlement.netAmount,
                final_total: grandTotal,
                fed_tax: settlement.fedTaxPercentage,
                extra_tax: settlement.extraTaxPercentage,
                fed_tax_amount: settlement.fedTaxAmount,
                extra_tax_amount: settlement.extraTaxAmount,

                advance_tax_section: advanceTax?.section ?? null,
                advance_tax_rate: advanceTaxRate,
                advance_tax_base: settlement.advanceTaxBase,
                advance_tax_amount: settlement.advanceTaxAmount,

                status: 'pending',
                payment_status: paymentStatus,
                paid_amount: settledAmount,
                auto_tax_rate: paymentData.autoTaxRate || 0,
                auto_tax_amount: paymentData.autoTaxAmount || 0,
                payment_method: paymentData.method,
                transaction_id: paymentData.transaction_id || null,
                account_id: paymentData.account_id || null,
                wallet_payment: paymentData.method === 'wallet',
                // Present only when the cashier accepted the over-limit dialog.
                credit_limit_override: paymentData.credit_limit_override === true,
                credit_change_to_wallet: mayCreditChange && walletChange > 0,
                change_amount: walletChange,
                cash_received_amount: isCash ? tenderedAmount : null,
                cash_change_amount: cashChange,

                delivery_charges: deliveryCharges,

                ...(isMultiBranchAddonEnabled() && isGeneral ? { allocation_mode: 'company' } : {}),

                // Same rule as automotive_id below: absent means "no opinion",
                // which defers to the customer's preference and then the
                // site-wide rule. PaymentModal only puts the key in the payload
                // when the cashier deliberately overrode this one sale.
                ...(typeof paymentData.post_sale_reminder === 'boolean'
                    ? { post_sale_reminder: paymentData.post_sale_reminder }
                    : {}),

                // Never send automotive_id: null — absent means "no automotive",
                // and the server treats a present-but-null value as an attempt to
                // link. The job card only rides along when the cashier actually
                // filled something in, so a parts-counter sale stays untouched.
                ...(tab.form.automotive_id
                    ? {
                          automotive_id: Number(tab.form.automotive_id),
                          ...(hasServiceRecordInput(tab.form.service_record)
                              ? { service_record: compactServiceRecord(tab.form.service_record) }
                              : {}),
                      }
                    : {}),

                delivery_information: paymentData.delivery
                    ? {
                          name: paymentData.delivery.name,
                          phone: paymentData.delivery.phone,
                          address: paymentData.delivery.address,
                          date: paymentData.delivery.date,
                          charges: deliveryCharges,
                      }
                    : null,

                driver_information: paymentData.delivery
                    ? {
                          driver_name: paymentData.delivery.driver_name,
                          driver_contact: paymentData.delivery.driver_contact,
                          vehicle_number: paymentData.delivery.vehicle_number,
                          courier_company: paymentData.delivery.courier_company,
                          tracking_number: paymentData.delivery.tracking_number,
                      }
                    : null,

                items: items.map((i) => ({
                    product_variant_id: i.id,
                    quantity: i.qty,
                    amount: i.rate,
                    discount_type: i.discount_type === 'percent' ? 'percent' : 'rs',
                    discount_value: Number(i.discount) || 0,
                    // The server verifies the damage price against the frozen
                    // tax snapshot and marks the unit sold in the same
                    // transaction as the stock allocation.
                    ...(i.damaged_item_id != null ? { damaged_item_id: i.damaged_item_id } : {}),
                    // Company stock search lets the server allocate across the pool
                    // from the initiation branch; single-branch carts pin the line.
                    ...(isGeneral ? {} : { branch_id: branchId }),
                })),
            };

            try {
                const res = await apiClient.post('sale/createSale', payload);

                if (res.data?.status === 'success' || res.data?.success) {
                    const newSaleId = res.data?.sale_id ?? null;
                    if (!Number.isSafeInteger(Number(newSaleId)) || Number(newSaleId) <= 0) {
                        const missingIdError = new Error('The server reported success without a valid sale ID. Verify the sale before retrying.');
                        missingIdError.code = 'SALE_ID_MISSING';
                        throw missingIdError;
                    }

                    useToast().displayToast({ message: 'Sale created successfully', variant: 'success' });
                    this.lastCreatedSaleId = newSaleId;

                    const serverAllocations = Array.isArray(res.data?.allocations) ? res.data.allocations : [];

                    if (serverAllocations.length > 0) {
                        await finalizeSaleAllocations(serverAllocations, {
                            originBranchId: isGeneral ? null : branchId,
                            reservedItems: isGeneral ? [] : items,
                        });
                    } else if (!isGeneral) {
                        await offlineDB.transaction('rw', offlineDB.branch_products, async () => {
                            for (const item of items) {
                                const key = `${branchId}-${Number(item.id)}`;
                                const record = await offlineDB.branch_products.get(key);
                                if (!record) continue;
                                record.real_qty = Math.max(0, normalizeQuantity(record.real_qty) - normalizeQuantity(item.qty));
                                record.reserved_qty = Math.max(
                                    0,
                                    normalizeQuantity(record.reserved_qty) - normalizeQuantity(item._lastQty ?? item.qty),
                                );
                                await offlineDB.branch_products.put(record);
                            }
                        });
                    }

                    // Clear the tab that actually submitted, even if another
                    // tab became active while the request was in flight.
                    if (this.tabs[tabId]) {
                        await this.clearTab(tabId);
                    } else if (!isGeneral) {
                        reservationGeneration++;
                        await this._refreshAllTabsMaxQty(branchId);
                    }
                    return newSaleId;
                } else {
                    useToast().displayToast({ message: res.data?.message || 'Sale failed', variant: 'error' });
                    const rejection = new Error(res.data?.message || 'Sale failed');
                    rejection.code = 'SALE_REJECTED';
                    throw rejection;
                }
            } catch (err) {
                if (err?.code === 'ERR_OFFLINE_WRITE') {
                    useToast().displayToast({
                        message: 'You are offline — changes are disabled until the connection returns.',
                        variant: 'warning',
                    });
                    return;
                }

                const status = err?.response?.status;
                const responseCode = err?.response?.data?.code;
                const isStockConflict = status === 409 && (responseCode === 'STOCK_CONFLICT' || Array.isArray(err?.response?.data?.availability));
                const isConcurrencyExhausted = responseCode === 'CONCURRENCY_RETRY_EXHAUSTED';

                if (responseCode === 'CREDIT_LIMIT_EXCEEDED') {
                    // Nothing was committed — the server checks credit before it
                    // touches stock. Keep the cart and payment open, and hand the
                    // server's figures back so the cashier can approve them.
                    this.creditLimitRejection = {
                        tabId,
                        context: err.response?.data?.credit ?? null,
                        message: err.response?.data?.message || 'This sale would exceed the customer credit limit.',
                    };
                    return;
                }

                if (isStockConflict || isConcurrencyExhausted) {
                    // Keep cart + payment open and reuse the same client_request_id.
                    await this.applyServerAvailability(err.response?.data ?? {});
                    const shortfall = Array.isArray(err.response?.data?.shortfall) ? err.response.data.shortfall : [];
                    const detail = shortfall
                        .slice(0, 2)
                        .map((row) => `#${row.product_variant_id}: only ${row.available ?? 0} available`)
                        .join('; ');
                    useToast().displayToast({
                        message: isConcurrencyExhausted
                            ? 'The system was busy. Nothing was charged — please try again.'
                            : detail
                              ? `Stock changed while completing this sale. ${detail}. Nothing was charged.`
                              : err.response?.data?.message || 'Stock changed while completing this sale. Nothing was charged.',
                        variant: 'warning',
                        duration: 10000,
                    });
                    if (!isGeneral && branchId) {
                        reservationGeneration++;
                        await this._refreshAllTabsMaxQty(branchId);
                    }
                    return;
                }

                const isAmbiguous =
                    err?.code === 'ECONNABORTED' ||
                    err?.code === 'ERR_NETWORK' ||
                    status === 408 ||
                    status === 429 ||
                    (status >= 500 && status < 600);

                if (isAmbiguous) {
                    useToast().displayToast({
                        message: 'Sale outcome unknown — verify before retrying. Stock was not released.',
                        variant: 'warning',
                        duration: 10000,
                    });
                    // Keep reservations; force a stock resync when possible
                    try {
                        await syncBranchStock(branchId, true);
                        reservationGeneration++;
                        await this._refreshAllTabsMaxQty(branchId);
                    } catch (syncErr) {
                        console.warn('post-timeout stock sync failed', syncErr);
                    }
                } else {
                    useToast().displayToast({
                        message: err.response?.data?.message || err.message || 'Network / Server error',
                        variant: 'error',
                    });
                    // Definite client rejection (typical 4xx except 408/429): roll back reservations
                    const isDefiniteRejection = err?.code === 'SALE_REJECTED' || (status && status >= 400 && status < 500);
                    if (branchId && items?.length && isDefiniteRejection && this.tabs[tabId]) {
                        // The rejected sale is still in the cart, so its local
                        // reservations must remain represented.
                        reservationGeneration++;
                        await this._refreshAllTabsMaxQty(branchId);
                    }
                }
            } finally {
                if (this.tabs[tabId]) {
                    this._setTab(tabId, {
                        ...this.tabs[tabId],
                        isSubmitting: false,
                    });
                }
            }
        },
    },
});
